import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import PlanPhoto from './PlanPhoto';
import { useToast } from '../ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';
import { investmentPlanSchema, PAYOUT_FREQUENCY_OPTIONS } from '../../lib/validators/investmentPlanSchema';
import {
  buildPlanFormData,
  useCreateInvestmentPlanMutation,
  useUpdateInvestmentPlanMutation,
} from '../../features/investmentPlans/investmentPlansApi';

const EMPTY_VALUES = {
  name: '',
  description: '',
  minimum_amount: '',
  maximum_amount: '',
  roi_percentage: '',
  duration_in_days: '',
  payout_frequency: 'monthly',
  photo: undefined,
};

/**
 * Shared create/edit form — `plan` null/undefined means create, a plan
 * object means edit. Both mutations send multipart/form-data (see
 * buildPlanFormData) since photo is a file upload, not JSON.
 *
 * Photo handling matches the docs' rules exactly: the file input's value is
 * only turned into a `photo` field on submit if the admin actually picked a
 * new file — leaving it untouched (edit mode, no new selection) must NOT
 * send an empty photo field, or the update would look like "clear the
 * photo" instead of "leave it alone".
 */
export default function PlanFormModal({ open, onClose, plan }) {
  const isEdit = !!plan;
  const toast = useToast();
  const [createPlan, { isLoading: isCreating }] = useCreateInvestmentPlanMutation();
  const [updatePlan, { isLoading: isUpdating }] = useUpdateInvestmentPlanMutation();
  const isLoading = isCreating || isUpdating;

  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm({ resolver: zodResolver(investmentPlanSchema), defaultValues: EMPTY_VALUES });

  // Re-seed the form every time the modal opens (covers both "open to
  // create" and "open to edit a different plan than last time").
  useEffect(() => {
    if (!open) return;
    reset({
      name: plan?.name ?? '',
      description: plan?.description ?? '',
      minimum_amount: plan?.minimum_amount ?? '',
      maximum_amount: plan?.maximum_amount ?? '',
      roi_percentage: plan?.roi_percentage ?? '',
      duration_in_days: plan?.duration_in_days ?? '',
      payout_frequency: plan?.payout_frequency ?? 'monthly',
      photo: undefined,
    });
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan]);

  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  const onPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    if (!file) {
      setValue('photo', undefined, { shouldValidate: true });
      setPreview(null);
      return;
    }
    setValue('photo', file, { shouldValidate: true });
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreview(url);
  };

  const onSubmit = async (values) => {
    const formData = buildPlanFormData(values);
    try {
      if (isEdit) {
        await updatePlan({ uuid: plan.id, formData }).unwrap();
        toast.push({ tone: 'success', message: 'Plan updated.' });
      } else {
        await createPlan(formData).unwrap();
        toast.push({ tone: 'success', message: 'Plan created.' });
      }
      onClose();
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
      } else {
        setError('root', {
          message: authErrorMessage(err, { fallback: `Could not ${isEdit ? 'update' : 'create'} this plan.` }),
        });
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit plan' : 'Create plan'}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Input label="Plan name" error={errors.name?.message} {...register('name')} />

        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1.5">Description</label>
          <textarea
            rows={3}
            className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong"
            {...register('description')}
          />
          {errors.description && <p className="mt-1.5 text-xs text-danger">{errors.description.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Minimum amount (₦)"
            type="number"
            step="0.01"
            error={errors.minimum_amount?.message}
            {...register('minimum_amount')}
          />
          <Input
            label="Maximum amount (₦)"
            type="number"
            step="0.01"
            helperText={!errors.maximum_amount ? 'Leave blank for no cap' : undefined}
            error={errors.maximum_amount?.message}
            {...register('maximum_amount')}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="ROI %" type="number" step="0.01" error={errors.roi_percentage?.message} {...register('roi_percentage')} />
          <Input
            label="Duration (days)"
            type="number"
            step="1"
            error={errors.duration_in_days?.message}
            {...register('duration_in_days')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1.5">Payout structure</label>
          <select
            className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong"
            {...register('payout_frequency')}
          >
            {PAYOUT_FREQUENCY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.payout_frequency && <p className="mt-1.5 text-xs text-danger">{errors.payout_frequency.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1.5">Photo</label>

          {isEdit && plan?.photo_url && !preview && (
            <div className="mb-2 flex items-center gap-3">
              <PlanPhoto src={plan.photo_url} alt={plan.name} className="h-16 w-16 rounded-lg" />
              <span className="text-xs text-foreground-subtle">Current photo — pick a file below to replace it.</span>
            </div>
          )}
          {preview && (
            <div className="mb-2">
              <PlanPhoto src={preview} alt="New photo preview" className="h-16 w-16 rounded-lg" />
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/jpg"
            onChange={onPhotoChange}
            className="w-full text-sm text-foreground-muted file:mr-3 file:rounded-lg file:border-0 file:bg-background-elevated file:px-3 file:py-2 file:text-sm file:font-medium file:text-foreground file:cursor-pointer hover:file:bg-background-surface"
          />
          <p className="mt-1.5 text-xs text-foreground-subtle">
            JPG or PNG, up to 5MB. {isEdit ? 'Leave empty to keep the current photo.' : 'Optional.'}
          </p>
          {errors.photo && <p className="mt-1.5 text-xs text-danger">{errors.photo.message}</p>}
        </div>

        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

        <Button type="submit" className="w-full" isLoading={isLoading}>
          {isEdit ? 'Save changes' : 'Create plan'}
        </Button>
      </form>
    </Modal>
  );
}
